import React, { useRef, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { Mic, MicOff, Video as VideoIcon, VideoOff, PhoneOff, HeartPulse, Timer, StickyNote, CheckCircle2, Users } from "lucide-react";
import { toast } from "sonner";

const ICE = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }, { urls: "stun:stun1.l.google.com:19302" }] };

function wsUrl(roomId) {
  const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws/rtc/${roomId}`;
}

export default function LiveRoom() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const localStream = useRef(null);
  const pc = useRef(null);
  const ws = useRef(null);

  const [booking, setBooking] = useState(null);
  const [stage, setStage] = useState("precall");
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [hr, setHr] = useState(72);
  const [seconds, setSeconds] = useState(0);
  const [peerConnected, setPeerConnected] = useState(false);
  const [notes, setNotes] = useState("Warm up 5 min · Focus: depth on squats · 4x8 @ RPE8");

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then((r) => setBooking(r.data)).catch(() => {});
  }, [bookingId]);

  const initMedia = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStream.current = stream;
      if (localRef.current) localRef.current.srcObject = stream;
      return stream;
    } catch {
      toast.error("Camera/mic access needed for the session");
      return null;
    }
  }, []);

  useEffect(() => {
    initMedia();
    return () => {
      localStream.current?.getTracks().forEach((t) => t.stop());
      ws.current?.close();
      pc.current?.close();
    };
  }, [initMedia]);

  const createPeer = useCallback((stream) => {
    const peer = new RTCPeerConnection(ICE);
    stream.getTracks().forEach((t) => peer.addTrack(t, stream));
    peer.onicecandidate = (e) => {
      if (e.candidate) ws.current?.send(JSON.stringify({ type: "ice", candidate: e.candidate }));
    };
    peer.ontrack = (e) => {
      if (remoteRef.current) remoteRef.current.srcObject = e.streams[0];
      setPeerConnected(true);
    };
    peer.onconnectionstatechange = () => {
      if (["disconnected", "failed", "closed"].includes(peer.connectionState)) setPeerConnected(false);
    };
    return peer;
  }, []);

  const startCall = async () => {
    const stream = localStream.current || (await initMedia());
    if (!stream || !booking?.room_id) return;
    pc.current = createPeer(stream);
    const socket = new WebSocket(wsUrl(booking.room_id));
    ws.current = socket;

    socket.onmessage = async (evt) => {
      const msg = JSON.parse(evt.data);
      if (msg.type === "joined") {
        // if peers already present, they will send us an offer; nothing to do
      } else if (msg.type === "peer-joined") {
        // we are the existing peer -> create the offer
        const offer = await pc.current.createOffer();
        await pc.current.setLocalDescription(offer);
        socket.send(JSON.stringify({ type: "offer", sdp: offer }));
      } else if (msg.type === "offer") {
        await pc.current.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        const answer = await pc.current.createAnswer();
        await pc.current.setLocalDescription(answer);
        socket.send(JSON.stringify({ type: "answer", sdp: answer }));
      } else if (msg.type === "answer") {
        await pc.current.setRemoteDescription(new RTCSessionDescription(msg.sdp));
      } else if (msg.type === "ice") {
        try { await pc.current.addIceCandidate(new RTCIceCandidate(msg.candidate)); } catch {}
      } else if (msg.type === "peer-left") {
        setPeerConnected(false);
        if (remoteRef.current) remoteRef.current.srcObject = null;
      }
    };
    socket.onerror = () => toast.error("Signaling connection error");
    setStage("live");
    toast.success("Room connected — waiting for the other participant");
  };

  useEffect(() => {
    if (stage !== "live") return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    const h = setInterval(async () => {
      try { const r = await api.get("/wearable"); if (r.data?.heart_rate) setHr(r.data.heart_rate); }
      catch { setHr(60 + Math.floor(Math.random() * 40)); }
    }, 3000);
    return () => { clearInterval(t); clearInterval(h); };
  }, [stage]);

  const toggleMic = () => {
    const track = localStream.current?.getAudioTracks()[0];
    if (track) { track.enabled = !track.enabled; setMicOn(track.enabled); }
  };
  const toggleCam = () => {
    const track = localStream.current?.getVideoTracks()[0];
    if (track) { track.enabled = !track.enabled; setCamOn(track.enabled); }
  };

  const endCall = async () => {
    ws.current?.close();
    pc.current?.close();
    localStream.current?.getTracks().forEach((t) => t.stop());
    if (booking) { try { await api.post(`/bookings/${booking.booking_id}/complete`); } catch {} }
    toast.success("Session ended");
    navigate("/dashboard");
  };

  const shareLink = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Room link copied — send it to your coach/client to join");
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  return (
    <div className="min-h-screen bg-[#121212] relative z-10">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF6B6B]">Live Studio</p>
            <h1 className="font-display font-bold text-2xl uppercase tracking-tight text-white">{booking?.discipline || "1-on-1 Session"}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="share-room-btn" onClick={shareLink} className="flex items-center gap-2 px-3 py-2 rounded-full bg-[#1E1E1E] border border-[#2A2A2A] text-white text-xs font-semibold hover:border-[#FF6B6B]/50 transition-colors">
              <Users className="w-4 h-4 text-[#4ECCD3]" /> Invite link
            </button>
            {stage === "live" && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF6B6B]/10 border border-[#FF6B6B]/30">
                <Timer className="w-4 h-4 text-[#FF6B6B]" />
                <span className="font-display font-bold text-lg text-[#FF6B6B]" data-testid="session-timer">{mmss}</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-4">
          <div className="lg:col-span-3 relative aspect-video rounded-2xl overflow-hidden border border-[#2A2A2A] bg-black">
            <video ref={remoteRef} autoPlay playsInline className={`w-full h-full object-cover ${stage === "live" && peerConnected ? "block" : "hidden"}`} />
            {stage === "precall" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-[#A0A0A0] text-sm mb-2">Pre-call check</p>
                <p className="text-white font-display font-bold text-xl uppercase">Get ready to train</p>
              </div>
            )}
            {stage === "live" && !peerConnected && (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="w-10 h-10 border-2 border-[#4ECCD3] border-t-transparent rounded-full animate-spin mb-3" />
                <p className="text-[#A0A0A0] text-sm">Waiting for the other participant to join…</p>
                <button onClick={shareLink} className="mt-3 text-xs text-[#FF6B6B] hover:underline">Copy invite link</button>
              </div>
            )}

            {stage === "live" && (
              <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-2 rounded-full bg-black/60 backdrop-blur" data-testid="hr-overlay">
                <HeartPulse className="w-5 h-5 text-[#FF3B30] animate-pulse" />
                <span className="font-display font-bold text-lg text-white">{hr}</span>
                <span className="text-xs text-[#A0A0A0]">BPM</span>
              </div>
            )}

            <div className="absolute bottom-4 right-4 w-40 aspect-video rounded-xl overflow-hidden border-2 border-[#FF6B6B]/50 bg-black">
              <video ref={localRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              {!camOn && <div className="absolute inset-0 bg-[#121212] flex items-center justify-center"><VideoOff className="w-6 h-6 text-[#A0A0A0]" /></div>}
            </div>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3">
              <button data-testid="toggle-mic-btn" onClick={toggleMic} className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${micOn ? "bg-white/10 text-white backdrop-blur" : "bg-[#FF3B30] text-white"}`}>{micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}</button>
              <button data-testid="toggle-cam-btn" onClick={toggleCam} className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${camOn ? "bg-white/10 text-white backdrop-blur" : "bg-[#FF3B30] text-white"}`}>{camOn ? <VideoIcon className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}</button>
              {stage === "precall" ? (
                <button data-testid="join-call-btn" onClick={startCall} className="px-6 h-12 rounded-full bg-[#FF6B6B] text-black font-bold uppercase tracking-wide text-sm hover:brightness-110 transition-all">Join Session</button>
              ) : (
                <button data-testid="end-call-btn" onClick={endCall} className="w-12 h-12 rounded-full bg-[#FF3B30] text-white flex items-center justify-center hover:brightness-110 transition-all"><PhoneOff className="w-5 h-5" /></button>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3"><StickyNote className="w-4 h-4 text-[#4ECCD3]" /><h3 className="font-bold text-white text-sm">Workout Notes</h3></div>
              <textarea data-testid="workout-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={6} className="w-full bg-[#121212] border border-[#2A2A2A] rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#FF6B6B] resize-none" />
            </div>
            <div className="bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl p-4">
              <h3 className="font-bold text-white text-sm mb-3">Session Info</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#A0A0A0]">Coach</span><span className="text-white">{booking?.instructor_name || "—"}</span></div>
                <div className="flex justify-between"><span className="text-[#A0A0A0]">Payment</span><span className="text-[#FF6B6B] flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {booking?.payment_status || "confirmed"}</span></div>
                <div className="flex justify-between"><span className="text-[#A0A0A0]">Peer</span><span className="text-white">{stage !== "live" ? "Ready" : peerConnected ? "Connected · WebRTC" : "Waiting…"}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
